import { useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { membershipRole, settableMemberStatus, type MembershipRole, type MembershipStatus, type SettableMemberStatus } from "@shyne/shared/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import { editsOf, editTitleOf, roleOptions } from "@/lib/permissions/member-roles";
import type { organization_type } from "@/models/organization.model";

const schema = z.object({
  role: membershipRole,
  status: settableMemberStatus.optional(),
});

type FormValues = z.infer<typeof schema>;

const STATUS_OPTIONS: { label: string; value: SettableMemberStatus }[] = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
];

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  organizationType: organization_type | undefined;
  currentRole: MembershipRole;
  currentStatus: MembershipStatus | undefined;
  onSubmit: (role: MembershipRole, status: SettableMemberStatus | undefined) => Promise<void>;
}

const settableOrNone = (status: MembershipStatus | undefined) => settableMemberStatus.safeParse(status).data;

export default function EditRoleStatusModal({ open, onOpenChange, organizationType, currentRole, currentStatus, onSubmit }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { role: currentRole, status: settableOrNone(currentStatus) },
  });

  useEffect(() => {
    if (open) form.reset({ role: currentRole, status: settableOrNone(currentStatus) });
  }, [open, currentRole, currentStatus, form]);

  const edits = editsOf(organizationType, currentStatus);
  const fields: FormFieldDef[] = [
    ...(edits.role ? [{ type: "select" as const, name: "role", label: "Role", required: true, options: roleOptions(organizationType) }] : []),
    ...(edits.status ? [{ type: "select" as const, name: "status", label: "Status", required: true, options: STATUS_OPTIONS }] : []),
  ];

  const handle = async (values: FormValues) => {
    await onSubmit(values.role, edits.status ? values.status : undefined);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editTitleOf(edits)}</DialogTitle>
        </DialogHeader>
        <FormCreator form={form} onSubmit={handle} formDefinition={fields} buttonText="Save" />
      </DialogContent>
    </Dialog>
  );
}
